import { useCallback, useState, useMemo } from 'react';
import {
  Grid,
  Panel,
  Box,
  Button,
  FilterButton,
  useTranslations,
  SearchableTextField,
  Tooltip,
  InviteUserIcon,
  useSnackbar,
  ActionButton,
  Divider,
  DeleteIcon,
  useMapKeyValueExtractor,
  PromptModal,
  usePopper,
  RenderIf,
  ShareIcon,
  derivePasswordKey,
  ISearchableTextFieldProps,
  IFilterButtonProps,
  encryptAsymmetric,
} from '@uniqkey-frontend/shared-app';
import {
  MeResponse,
  KeysState,
} from '@uniqkey-backend-partner/api-client';
import ACLEnum from '../../enums/ACLEnum';
import { useUser } from '../../contexts/UserContext';
import usePartnerUsersForAdminTable from '../../hooks/tables/usePartnerUsersForAdminTable';
import PartnerUsersForAdminTable, {
  IPartnerUsersForAdminTableRow,
} from '../../components/tables/PartnerUsersForAdminTable';
import usePartnerUsersAPI from '../../hooks/usePartnerUsersAPI';
import { useDeletePartnerUsers } from '../../hooks/reactQuery';
import PartnerUsersForAdminListFilter, {
  IPartnerUsersForAdminListFilterSubmitResult,
} from './components/PartnerUsersForAdminListFilter';
import CreatePartnerUserModal, {
  ICreatePartnerUserModalFormValue,
} from '../PartnerUsersPage/components/CreatePartnerUserModal';
import SharePartnerPrivateKeyModal, {
  ISharePartnerPrivateKeyModalFormValue,
} from './components/SharePartnerPrivateKeyModal';
import KeysManager from '../../services/keysManager';
import {
  KeysManagerCannotDecryptPartnerUserKeysError,
  KeysManagerFetchPartnerUserKeysError,
} from '../../services/keysManager/partnerUserKeys';
import { generateTooltipTitle } from '../../helpers/tooltips';

const PartnerUsersForAdminPage = () => {
  const { t } = useTranslations();
  const { createPartnerUser } = usePartnerUsersAPI();
  const { showSuccess, showError } = useSnackbar();
  const { currentUser, userCan } = useUser();
  const {
    partnerId, role, id: currentUserId, hasPartnerKeys,
  } = currentUser ?? {} as MeResponse;

  const [isCreatePartnerUserModalOpen, setIsCreatePartnerUserModalOpen] = useState(false);
  const [isCreatePartnerUserLoading, setIsCreatePartnerUserLoading] = useState(false);
  const [isDeletePartnerUsersModalOpen, setIsDeletePartnerUsersModalOpen] = useState(false);
  const [
    isSharePartnerPrivateKeyModalOpen,
    setIsSharePartnerPrivateKeyModalOpen,
  ] = useState(false);
  const [isSharePartnerPrivateKeyLoading, setIsSharePartnerPrivateKeyLoading] = useState(false);

  const {
    selectedPartnerUsersForAdmin,
    refetch,
    searchQuery,
    setSearchQuery,
    resetActivePage,
    resetSelectedRows,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    ...restTableProps
  } = usePartnerUsersForAdminTable({
    noDataMessageKey: 'partnerUsersForAdminPage.noData',
  });

  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const handleFilterClose = useCallback(() => setIsFilterOpen(false), [setIsFilterOpen]);

  const {
    values: selectedPartnerUsersForAdminAsObjects, keys: selectedPartnerUsersForAdminIds,
  } = useMapKeyValueExtractor<
    IPartnerUsersForAdminTableRow
  >(selectedPartnerUsersForAdmin);

  const {
    mutate: deletePartnerUsers,
    isLoading: isDeletePartnerUsersLoading,
  } = useDeletePartnerUsers();

  const handleCreatePartnerUserModalOpen = useCallback(
    () => setIsCreatePartnerUserModalOpen(true),
    [],
  );

  const handleCreatePartnerUserModalClose = useCallback(
    () => setIsCreatePartnerUserModalOpen(false),
    [],
  );

  const handleDeletePartnerUsersModalOpen = useCallback(
    () => setIsDeletePartnerUsersModalOpen(true),
    [],
  );

  const handleDeletePartnerUsersModalClose = useCallback(
    () => setIsDeletePartnerUsersModalOpen(false),
    [],
  );

  const handleSharePartnerPrivateKeyModalOpen = useCallback(
    () => setIsSharePartnerPrivateKeyModalOpen(true),
    [],
  );

  const handleSharePartnerPrivateKeyModalClose = useCallback(
    () => setIsSharePartnerPrivateKeyModalOpen(false),
    [],
  );

  const isCurrentUserSelected = selectedPartnerUsersForAdmin.has(currentUserId);
  const canDeletePartnerUser = userCan(ACLEnum.PartnerUserDelete);

  const {
    isDeletePartnerUserDisabled,
    isSharePartnerKeysDisabled,
  } = useMemo(() => {
    if (!selectedPartnerUsersForAdminAsObjects.length || isCurrentUserSelected) {
      return {
        isDeletePartnerUserDisabled: true,
        isSharePartnerKeysDisabled: true,
      };
    }
    let disableDeletePartnerUser = false;
    let disableSharePartnerKeys = false;
    if (!canDeletePartnerUser) {
      disableDeletePartnerUser = true;
    }
    if (hasPartnerKeys) {
      disableSharePartnerKeys = selectedPartnerUsersForAdminAsObjects.some(
        ({ keysState }) => keysState !== KeysState.PartnerUserToPartnerKeyUnavailable,
      );
    } else {
      disableSharePartnerKeys = true;
    }
    return {
      isDeletePartnerUserDisabled: disableDeletePartnerUser,
      isSharePartnerKeysDisabled: disableSharePartnerKeys,
    };
  }, [
    canDeletePartnerUser,
    hasPartnerKeys,
    isCurrentUserSelected,
    selectedPartnerUsersForAdminAsObjects,
  ]);

  const {
    deletePartnerUserTooltipTitle,
    sharePartnerKeysTooltipTitle,
  } = useMemo(() => {
    const deletePartnerUserTitle = generateTooltipTitle({
      selectedDataLength: selectedPartnerUsersForAdminAsObjects.length,
      t,
      isDisabled: isDeletePartnerUserDisabled,
      key: 'partnerUsersForAdminPage.delete',
    });
    const sharePartnerKeysTitle = generateTooltipTitle({
      selectedDataLength: selectedPartnerUsersForAdminAsObjects.length,
      t,
      isDisabled: isSharePartnerKeysDisabled,
      key: 'partnerUsersForAdminPage.share',
    });

    return {
      deletePartnerUserTooltipTitle: deletePartnerUserTitle,
      sharePartnerKeysTooltipTitle: sharePartnerKeysTitle,
    };
  }, [
    selectedPartnerUsersForAdminAsObjects.length,
    t,
    isSharePartnerKeysDisabled,
    isDeletePartnerUserDisabled,
  ]);

  const TABLE_OPTIONS = useMemo(() => ({
    selection: canDeletePartnerUser,
  }), [canDeletePartnerUser]);

  const handleCreatePartnerUser = useCallback(async (
    partnerUser: ICreatePartnerUserModalFormValue,
  ) => {
    try {
      setIsCreatePartnerUserLoading(true);
      await createPartnerUser({ ...partnerUser, partnerId });
      showSuccess({
        text: t('createPartnerUserModal.partnerUserCreated'),
      });
      handleCreatePartnerUserModalClose();
      refetch();
    } catch (error) {
      showError({ text: t('common.somethingWentWrong') });
    } finally {
      setIsCreatePartnerUserLoading(false);
    }
  }, [
    partnerId,
    createPartnerUser,
    handleCreatePartnerUserModalClose,
    refetch,
    showError,
    showSuccess,
    t,
  ]);

  const handleDeletePartnerUsers = useCallback(async () => {
    deletePartnerUsers({
      partnerUserIds: selectedPartnerUsersForAdminIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'deletePartnerUsersModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({ text: t('deletePartnerUsersModal.errorMessage', { count: failCount }) });
        }
        handleDeletePartnerUsersModalClose();
        resetSelectedRows();
        resetActivePage();
        refetch();
      },
      onError: () => showError({ text: t('common.somethingWentWrong') }),
    });
  }, [
    selectedPartnerUsersForAdminIds,
    deletePartnerUsers,
    handleDeletePartnerUsersModalClose,
    resetSelectedRows,
    resetActivePage,
    refetch,
    showError,
    showSuccess,
    t,
  ]);

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((
    updatedValues: IPartnerUsersForAdminListFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const handleSharePartnerPrivateKey = useCallback(async (
    form: ISharePartnerPrivateKeyModalFormValue,
  ) => {
    try {
      setIsSharePartnerPrivateKeyLoading(true);
      const { password } = form;
      const derivedMasterPassword = await derivePasswordKey(password, currentUserId);

      const fetchedPartnerUserKeysResult = await KeysManager.fetchPartnerUserKeys(
        derivedMasterPassword,
      );
      if (fetchedPartnerUserKeysResult instanceof KeysManagerFetchPartnerUserKeysError) {
        showError({ text: t('common.somethingWentWrong') });
        return;
      }
      if (fetchedPartnerUserKeysResult instanceof KeysManagerCannotDecryptPartnerUserKeysError) {
        showError({ text: t('sharePartnerPrivateKeyModal.incorrectPassword') });
        return;
      }

      const fetchedPartnerKeys = await KeysManager.fetchPartnerKeys(fetchedPartnerUserKeysResult);
      if (!fetchedPartnerKeys) {
        showError({ text: t('common.somethingWentWrong') });
        return;
      }

      const { privateKey: partnerPrivateKey, partnerKeyId } = fetchedPartnerKeys;
      if (!partnerPrivateKey) {
        showError({ text: t('common.somethingWentWrong') });
        return;
      }

      const fetchedPartnerUsersPublicKeys = await KeysManager.fetchPartnerUsersPublicKeys(
        { publicUserIds: selectedPartnerUsersForAdminIds },
      );

      const sharePartnerPrivateKeyData = await Promise.all(
        fetchedPartnerUsersPublicKeys.map(async (partnerUserPublicKeys) => {
          const { publicKey, partnerUserId } = partnerUserPublicKeys;

          const encryptedPrivateKey = await encryptAsymmetric({
            string: partnerPrivateKey,
            publicKey,
          });

          return {
            partnerUserId,
            partnerPrivateKey: encryptedPrivateKey,
          };
        }),
      );

      const { successCount, failCount } = await KeysManager.sharePartnerPrivateKey(
        { partnerKeyId, data: sharePartnerPrivateKeyData },
      );

      if (successCount) {
        showSuccess(
          { text: t('sharePartnerPrivateKeyModal.successMessage', { count: successCount }) },
        );
      }

      if (failCount) {
        showError({ text: t('sharePartnerPrivateKeyModal.errorMessage', { count: failCount }) });
      }

      handleSharePartnerPrivateKeyModalClose();
      resetSelectedRows();
      refetch();
    } catch (e) {
      showError({ text: t('common.somethingWentWrong') });
    } finally {
      setIsSharePartnerPrivateKeyLoading(false);
    }
  }, [
    handleSharePartnerPrivateKeyModalClose,
    currentUserId,
    selectedPartnerUsersForAdminIds,
    showError,
    t,
    resetSelectedRows,
    refetch,
    showSuccess,
  ]);

  return (
    <Grid container flexDirection="column" className="min-height-100-percent">
      <Grid item mb={1}>
        <Panel>
          <Box p={1}>
            <Grid container justifyContent="space-between" alignItems="stretch">
              <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
                <Grid item>
                  <Tooltip title={t('common.filter')}>
                    <FilterButton
                      isFilterActive={isFilterActive}
                      numberOfActiveFilters={numberOfActiveFilters}
                      selected={isFilterOpen}
                      onChange={toggleIsFilterOpen}
                    />
                  </Tooltip>
                </Grid>
                <Grid item my={0.5}>
                  <Divider orientation="vertical" />
                </Grid>
                <Grid item alignSelf="center">
                  <Tooltip title={deletePartnerUserTooltipTitle}>
                    <ActionButton
                      width={40}
                      height={40}
                      onClick={handleDeletePartnerUsersModalOpen}
                      disabled={isDeletePartnerUserDisabled}
                    >
                      <DeleteIcon />
                    </ActionButton>
                  </Tooltip>
                </Grid>
                <Grid item alignSelf="center">
                  <Tooltip title={sharePartnerKeysTooltipTitle}>
                    <ActionButton
                      width={40}
                      height={40}
                      onClick={handleSharePartnerPrivateKeyModalOpen}
                      disabled={isSharePartnerKeysDisabled}
                    >
                      <ShareIcon />
                    </ActionButton>
                  </Tooltip>
                </Grid>
                <Grid item my={0.5}>
                  <Divider orientation="vertical" />
                </Grid>
              </Grid>
              <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
                <Grid item>
                  <SearchableTextField
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={t('common.search')}
                  />
                </Grid>
                <RenderIf condition={userCan(ACLEnum.PartnerUserCreate)}>
                  <Grid ml={3} item>
                    <Button
                      icon={<InviteUserIcon />}
                      onClick={handleCreatePartnerUserModalOpen}
                    >
                      {t('partnerUsersForAdminPage.createPartnerUserButton')}
                    </Button>
                  </Grid>
                </RenderIf>
              </Grid>
            </Grid>
          </Box>
        </Panel>
      </Grid>
      <Grid item xs>
        <Panel>
          <PartnerUsersForAdminTable
            options={TABLE_OPTIONS}
            selectedPartnerUsersForAdmin={selectedPartnerUsersForAdmin}
            {...restTableProps}
          />
        </Panel>
      </Grid>
      {isCreatePartnerUserModalOpen && (
        <CreatePartnerUserModal
          isOpen={isCreatePartnerUserModalOpen}
          isLoading={isCreatePartnerUserLoading}
          onSubmit={handleCreatePartnerUser}
          onClose={handleCreatePartnerUserModalClose}
          role={role}
        />
      )}
      {isDeletePartnerUsersModalOpen && (
        <PromptModal
          open={isDeletePartnerUsersModalOpen}
          onClose={handleDeletePartnerUsersModalClose}
          onSubmit={handleDeletePartnerUsers}
          title={t(
            'deletePartnerUsersModal.title',
            { count: selectedPartnerUsersForAdmin.size },
          )}
          description={t('deletePartnerUsersModal.description')}
          approvalButtonText="common.delete"
          list={selectedPartnerUsersForAdminAsObjects}
          renderField="name"
          renderKey="partnerUserId"
          isLoading={isDeletePartnerUsersLoading}
        />
      )}
      {isSharePartnerPrivateKeyModalOpen && (
        <SharePartnerPrivateKeyModal
          isOpen={isSharePartnerPrivateKeyModalOpen}
          onClose={handleSharePartnerPrivateKeyModalClose}
          isLoading={isSharePartnerPrivateKeyLoading}
          onSubmit={handleSharePartnerPrivateKey}
        />
      )}
      <PartnerUsersForAdminListFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
        role={role}
      />
    </Grid>
  );
};

export default PartnerUsersForAdminPage;
